/**
 * Route stop for runner collections (offline-first dual-ack clear).
 */

export const ROUTE_STOP_STATUSES = [
  'pending',
  'in_progress',
  'awaiting_clear',
  'cleared',
  'skipped',
] as const;

export type RouteStopStatus = (typeof ROUTE_STOP_STATUSES)[number];

export const ROUTE_STOP_PRIORITIES = ['normal', 'high'] as const;
export type RouteStopPriority = (typeof ROUTE_STOP_PRIORITIES)[number];

export const ROUTE_STOP_KINDS = ['lister', 'house', 'adhoc'] as const;
export type RouteStopKind = (typeof ROUTE_STOP_KINDS)[number];

export interface RouteStop {
  id: string;
  houseId: string;
  /** Empty for house / adhoc stops. */
  listerId: string;
  runnerId: string;
  name: string;
  addr: string;
  lat?: number | null;
  lng?: number | null;
  expectedAmount: number;
  collectedAmount?: number | null;
  listerDeliveredAmount?: number | null;
  runnerAmountConfirmed: boolean;
  listerAmountConfirmed: boolean;
  runnerConfirmedAt?: number | null;
  listerConfirmedAt?: number | null;
  status: RouteStopStatus;
  skipReason?: string | null;
  skippedBy?: string | null;
  /** Runner-requested skip awaiting banker approval. */
  skipRequestStatus?: 'pending' | null;
  skipRequestReason?: string | null;
  skipRequestedBy?: string | null;
  skipRequestedAt?: number | null;
  skipReviewedBy?: string | null;
  skipReviewedAt?: number | null;
  /** Banker self-collect: who secured the cash. */
  securedBy?: string | null;
  securedAt?: number | null;
  securedByRole?: 'banker' | 'god' | string | null;
  /** Banker acknowledged dual-ack amount mismatch. */
  discrepancyReviewedAt?: number | null;
  discrepancyReviewedBy?: string | null;
  discrepancyNote?: string | null;
  dayKey?: string;
  collectedAt?: number | null;
  /** Legacy mirror: true when status === cleared */
  done?: boolean;
  /** Legacy single amount field — prefer expectedAmount */
  amount?: number;
  incidentNote?: string | null;
  incidentAt?: number | null;
  /** Banker/God visit priority — high stops are optimized first. */
  priority?: RouteStopPriority;
  /** Visit order after optimize (1-based). */
  sequence?: number | null;
  /** Origin of the stop: lister pickup, house HQ, or ad-hoc. */
  kind?: RouteStopKind;
}

export type RouteStopInput = Partial<RouteStop> & {
  id: string;
  houseId?: string;
  listerId?: string;
  runnerId?: string;
  name?: string;
  addr?: string;
};

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function bool(v: unknown, fallback = false): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * Normalize legacy `{ amount, done }` seeds and partial docs into RouteStop.
 */
export function normalizeRouteStop(raw: RouteStopInput | Record<string, unknown>): RouteStop {
  const r = raw as Record<string, unknown>;
  const expected =
    numOrNull(r.expectedAmount) ??
    numOrNull(r.amount) ??
    0;
  const runnerAck = bool(r.runnerAmountConfirmed, Boolean(r.done));
  const listerAck = bool(r.listerAmountConfirmed, Boolean(r.done));
  let status = String(r.status ?? '') as RouteStopStatus;
  if (!ROUTE_STOP_STATUSES.includes(status)) {
    if (r.done === true || (runnerAck && listerAck)) status = 'cleared';
    else if (r.skipReason || r.skippedBy) status = 'skipped';
    else if (runnerAck || listerAck) status = 'awaiting_clear';
    else status = 'pending';
  }
  const cleared = status === 'cleared' || (runnerAck && listerAck && status !== 'skipped');
  if (cleared && status !== 'skipped') status = 'cleared';

  const priorityRaw = String(r.priority ?? 'normal').toLowerCase();
  const priority: RouteStopPriority = priorityRaw === 'high' ? 'high' : 'normal';
  const sequence = numOrNull(r.sequence);
  const kindRaw = String(r.kind ?? '').toLowerCase();
  const kind: RouteStopKind = ROUTE_STOP_KINDS.includes(kindRaw as RouteStopKind)
    ? (kindRaw as RouteStopKind)
    : (r.listerId ? 'lister' : 'adhoc');

  return {
    id: String(r.id ?? ''),
    houseId: String(r.houseId ?? ''),
    listerId: String(r.listerId ?? ''),
    runnerId: String(r.runnerId ?? ''),
    name: String(r.name ?? ''),
    addr: String(r.addr ?? ''),
    lat: numOrNull(r.lat),
    lng: numOrNull(r.lng),
    expectedAmount: expected,
    collectedAmount: numOrNull(r.collectedAmount) ?? (cleared ? expected : null),
    listerDeliveredAmount: numOrNull(r.listerDeliveredAmount) ?? (cleared ? expected : null),
    runnerAmountConfirmed: cleared ? true : runnerAck,
    listerAmountConfirmed: cleared ? true : listerAck,
    runnerConfirmedAt: numOrNull(r.runnerConfirmedAt),
    listerConfirmedAt: numOrNull(r.listerConfirmedAt),
    status,
    skipReason: r.skipReason != null ? String(r.skipReason) : null,
    skippedBy: r.skippedBy != null ? String(r.skippedBy) : null,
    skipRequestStatus: String(r.skipRequestStatus ?? '') === 'pending' ? 'pending' : null,
    skipRequestReason: r.skipRequestReason != null ? String(r.skipRequestReason) : null,
    skipRequestedBy: r.skipRequestedBy != null ? String(r.skipRequestedBy) : null,
    skipRequestedAt: numOrNull(r.skipRequestedAt),
    skipReviewedBy: r.skipReviewedBy != null ? String(r.skipReviewedBy) : null,
    skipReviewedAt: numOrNull(r.skipReviewedAt),
    securedBy: r.securedBy != null ? String(r.securedBy) : null,
    securedAt: numOrNull(r.securedAt),
    securedByRole: r.securedByRole != null ? String(r.securedByRole) : null,
    discrepancyReviewedAt: numOrNull(r.discrepancyReviewedAt),
    discrepancyReviewedBy: r.discrepancyReviewedBy != null ? String(r.discrepancyReviewedBy) : null,
    discrepancyNote: r.discrepancyNote != null ? String(r.discrepancyNote) : null,
    dayKey: r.dayKey != null ? String(r.dayKey) : undefined,
    collectedAt: numOrNull(r.collectedAt),
    done: status === 'cleared',
    amount: expected,
    incidentNote: r.incidentNote != null ? String(r.incidentNote) : null,
    incidentAt: numOrNull(r.incidentAt),
    priority,
    sequence,
    kind,
  };
}

export function createRouteStop(fields: RouteStopInput): RouteStop {
  return normalizeRouteStop({
    ...fields,
    expectedAmount: fields.expectedAmount ?? fields.amount ?? 0,
    runnerAmountConfirmed: fields.runnerAmountConfirmed ?? false,
    listerAmountConfirmed: fields.listerAmountConfirmed ?? false,
    status: fields.status ?? 'pending',
  });
}

/**
 * True WGS84 coords suitable for maps / deep links.
 * Rejects historic demo doodle points (lat/lng both in ~20–80 positive).
 */
export function stopHasNavigableCoords(stop: Pick<RouteStop, 'lat' | 'lng'>): boolean {
  if (typeof stop.lat !== 'number' || typeof stop.lng !== 'number') return false;
  if (!Number.isFinite(stop.lat) || !Number.isFinite(stop.lng)) return false;
  if (Math.abs(stop.lat) > 90 || Math.abs(stop.lng) > 180) return false;
  // Real Havana ~23.1 N, -82.3 W — positive lng with mid-range lat = old doodle.
  if (stop.lat >= 15 && stop.lat <= 85 && stop.lng > 0 && stop.lng < 100) return false;
  return true;
}

export const stopHasCoords = stopHasNavigableCoords;
